import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  onClose: () => void;
  onGifClick?: () => void;
}

// Basic emoji categories for adult festival users
const EMOJI_CATEGORIES = {
  faces: {
    name: 'Gezichten',
    emojis: ['😀', '😂', '🥳', '😎', '🤩', '😍', '😘', '🙄', '😅', '😊', '😉', '😋', '😜', '🤤', '😴', '🤯']
  },
  party: {
    name: 'Feest',
    emojis: ['🎉', '🍻', '🍺', '🎵', '🎶', '💃', '🕺', '🔥', '⚡', '✨', '🎊', '🎈', '🥂', '🍾', '🎤', '🎧']
  },
  hearts: {
    name: 'Liefde',
    emojis: ['❤️', '💕', '💖', '💯', '🥰', '😻', '💋', '💘', '💝', '💞', '💓', '💗', '💙', '💚', '💛', '🧡']
  },
  gestures: {
    name: 'Gebaren',
    emojis: ['👍', '✌️', '🤘', '🙌', '💪', '👌', '👎', '🤟', '🤜', '🤛', '👏', '🙏', '🤝', '✊', '👊', '🖕']
  }
};

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, onClose, onGifClick }) => {
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof EMOJI_CATEGORIES>('faces');
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji);
    onClose(); // Close picker after selection
  };

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full left-0 mb-2 w-80 bg-surface rounded-card border border-white/[.08] shadow-panel overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/[.06]">
        <h3 className="text-sm font-semibold text-fg-bright">Kies een emoji</h3>
        <button
          onClick={onClose}
          className="text-fg-faint hover:text-fg-bright transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Category tabs */}
      <div className="flex border-b border-white/[.06]">
        {Object.entries(EMOJI_CATEGORIES).map(([key, category]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key as keyof typeof EMOJI_CATEGORIES)}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              selectedCategory === key
                ? 'text-neon-cyan border-b-2 border-neon-cyan bg-white/[.04]'
                : 'text-fg-muted hover:text-fg-soft hover:bg-white/[.04]'
            }`}
          >
            {category.name}
          </button>
        ))}
        {/* GIF tab: routes to the GIF picker (premium) or the upgrade flow */}
        {onGifClick && (
          <button
            onClick={onGifClick}
            className="flex-1 px-3 py-2 text-xs font-bold text-neon-pink hover:text-neon-pink-soft hover:bg-white/[.04] transition-colors"
          >
            GIF
          </button>
        )}
      </div>

      {/* Emoji grid */}
      <div className="p-3">
        <div className="grid grid-cols-8 gap-1">
          {EMOJI_CATEGORIES[selectedCategory].emojis.map((emoji, index) => (
            <button
              key={`${emoji}-${index}`}
              onClick={() => handleEmojiClick(emoji)}
              className="w-8 h-8 flex items-center justify-center text-lg hover:bg-white/[.08] rounded transition-colors"
              title={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Footer tip */}
      <div className="px-3 py-2 bg-white/[.03] border-t border-white/[.06]">
        <p className="text-xs text-fg-faint text-center">
          Klik op een emoji om toe te voegen aan je bericht
        </p>
      </div>
    </div>
  );
};

export default EmojiPicker;